/**
 * 回忆（/memories）：新版三栏（地图 · 通讯器 · 回忆）的第三栏。全屏页，自带 WorldGate，底部挂新版三栏预览
 * （方案 docs/product/PETSOUL-PLAYER-UI-MAP-FIRST-PLAN-2026-09-24.md 第 2、7.2、19 节）。
 * - 只放现在就有后端能力的入口：生活片段、打工记录、明信片与小收藏、照片、旅程与攻略手账；不放“即将开放”之类的占位。
 *   “照片”打开正式相册（/album，P3：用户 10-03「做正式的相册页」）：只有画好的照片，按日子收着；大字是相册里看得到的张数，
 *   小字是拍照记录里还在画的几张（在画的不叫照片，相册页顶上有“拍照记录”入口）。
 *   生活片段（/timeline）排最前、横跨两列（方案 7.2 把它列在回忆的第一条）；它的入口不带一句话，不为这句话多发一次请求。
 * - 入口上的一句话只来自对应服务的结果；请求失败（包括演示模式没有这项数据）时只隐藏这句话，入口照常，不写错误文字。
 * - 查询键与各目的页共用（queryKeys），进到二级页时不必重取。
 * - 顶栏用全站那一种（UI-BASE-01b，总账 V-03）：原来 28px 大标题下面压一道纸胶带（荧光笔涂抹）、右上角放头像；
 *   现在是 TopBar（标题、副标题、左上返回），纸胶带删掉；顶栏右边只放动作，头像拿掉（名字写在副标题里）。
 * - “旅程与攻略手账”和攻略手账页一致（巡检 R14-E01：原来只数旧攻略，有计划手账也写“还没有手账”）：旧攻略（GET /guides）加上
 *   攻略页“想去 / 准备中”里写好了的计划——当前心愿已发布的计划（GET /travel/wish，plan_revision 不为空；计划发布时就写好了计划手账），
 *   演示模式再加上攻略页上那几份演示计划（演示计划不进 live）；小字是攻略页那张心愿卡的标题。心愿、演示计划还在读或心愿读不到时这一句不写。
 * - 入口下面是“最近一趟旅行”（TRV-06，./TripMemory）：只收真事件——只在最近那份计划有回忆手账时出现，章只来自真实到访；
 *   读不到就不出现，入口照常。
 */
import { useCallback, useSyncExternalStore, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { SourceLink, readSceneEntry, preserveSceneState } from "@/shared/ui/sceneNavigation";
import type { JobRecord, TravelWish } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { ErrorState, Icon, LoadingState, Page, TopBar, type IconName } from "@/shared/ui";
import { usePrimaryTab } from "@/app/primaryTab";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "./currentPet";
import { albumCountKey } from "./album";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { useCurrentWish, useDemoPlans, type Read } from "@/features/journey/travelPlan/data";
import { viewOfWish, type PlanBundle } from "@/features/journey/travelPlan/model";
import { jobStatusText, latestJob, photoCounts } from "./facts";
import { TripMemory } from "./TripMemory";
import { publishedPlanId } from "./travelMemory";
import "./memories.css";

type Tone = "leaf" | "sun" | "coral" | "sky";
/** 入口上的事实：粗体一句（数字用衬线体），需要时再加一行小字。 */
type Fact = { main: ReactNode; detail?: string };

export function MemoriesPage() {
  return (
    <WorldGate>
      <MemoriesBody />
    </WorldGate>
  );
}

function MemoriesBody() {
  const current = useCurrentPet();
  const pet = current.status === "ready" ? current.pet : null;
  // 外壳不再挂底栏（10-03）：回忆是第二层，左上角回到 TA 此刻的主屏（在家小屋、在外地图）
  const primary = usePrimaryTab();
  return (
    <>
      <Page className="ps-mem">
        <TopBar
          title="回忆"
          subtitle={current.status === "ready" ? `${pet?.name ?? "TA "}的日子，都攒在这里。` : undefined}
          sourceBack={{ fallback: primary.to, fallbackLabel: `回到${primary.label}` }}
        />
        {current.status === "pending" ? (
          <LoadingState lines={3} label="正在翻开回忆…" />
        ) : current.status === "error" ? (
          <ErrorState error={current.error} onRetry={current.retry} />
        ) : (
          <>
            <MemoryEntries pet={pet} userId={current.userId} household={current.household} />
            <TripMemory />
          </>
        )}
      </Page>
    </>
  );
}

function MemoryEntries({ pet, userId, household }: { pet: CurrentPet | null; userId: string | null; household: Extract<ReturnType<typeof useCurrentPet>, { status: "ready" }>["household"] }) {
  const facts = useMemoryFacts(pet?.petId ?? null, userId, household);
  return (
    <nav className="ps-mem-grid" aria-label="回忆里的入口">
      <EntryCard wide to="/timeline" icon="diary" tone="coral" title="生活片段" desc="TA 走过的日子，都按时间记着" fact={null} />
      <EntryCard to="/life?tab=jobs&from=memories" icon="work" tone="sun" title="打工记录" desc="TA 出门打过的工" fact={facts.jobs} />
      <EntryCard to="/collection" icon="postcard" tone="coral" title="明信片与小收藏" desc="旅途上带回来的" fact={facts.collection} />
      <EntryCard to="/album" icon="album" tone="sky" title="照片" desc="TA 带回来的照片，按日子收着" fact={facts.photos} />
      <EntryCard to="/guides" icon="journey" tone="leaf" title="旅程与攻略手账" desc="TA 出门前写的小攻略" fact={facts.guides} />
    </nav>
  );
}

/** 五个入口各自的一句话。没有结果（还在读、读失败、演示模式没有这项数据）时为 null：页面只隐藏这句话。 */
function useMemoryFacts(petId: string | null, userId: string | null, household: Extract<ReturnType<typeof useCurrentPet>, { status: "ready" }>["household"]) {
  const services = useServices();
  const live = env.dataMode === "live";
  const owner = userId ?? "-";
  const pet = petId ?? "-";
  const enabled = Boolean(petId) && (!live || Boolean(userId));
  const jobs = useQuery({ queryKey: queryKeys.jobsFor(owner, pet), queryFn: ({ signal }) => services.life.jobs(pet, signal), enabled });
  const collectionQueryKey = live ? [...queryKeys.collectionFor(owner, pet), "reader", household?.household_id ?? null, household?.home_id ?? null] : queryKeys.collection;
  const collectionEnabled = Boolean(petId) && (!live || Boolean(userId?.trim() && petId?.trim() && household?.household_id?.trim() && household?.home_id?.trim()));
  const collection = useQuery({
    // 与收藏页同一个键：fixture 用公共键，live 按已确认的账号、宠物、家庭与住所分开。
    queryKey: collectionQueryKey,
    queryFn: ({ signal }) => services.economy.collection(petId, signal),
    enabled: collectionEnabled,
  });
  const photos = useQuery({ queryKey: queryKeys.photoRequestsFor(owner, pet), queryFn: ({ signal }) => services.pets.photoRequests(pet, signal), enabled });
  // 相册里看得到几张（只要第一页的 total_visible）：照片卡的大字只数画好的
  const album = useQuery({ queryKey: albumCountKey(owner, pet), queryFn: ({ signal }) => services.pets.album(pet, { limit: 1 }, signal), enabled });
  const guidesQueryKey = [...queryKeys.guidesFor(owner, pet), "reader", household?.household_id ?? null, household?.home_id ?? null];
  const guidesEnabled = Boolean(petId) && (!live || Boolean(userId?.trim() && petId?.trim() && household?.household_id?.trim() && household?.home_id?.trim()));
  const guides = useQuery({ queryKey: guidesQueryKey, queryFn: ({ signal }) => services.transport.guides(petId, signal), enabled: guidesEnabled });
  // 攻略手账页“想去 / 准备中”那几张（当前心愿；演示时还有几份演示计划）：与攻略页同一个读法、同一个查询键；宠物名字的取法也与攻略页相同。
  const wish = useCurrentWish();
  const demo = useDemoPlans();
  const petName = useOptionalCurrentHousehold()?.pet?.name ?? null;

  // Query observers batch updates. Read the exact cache state so a known failed read cannot project old data.
  const jobData = useCurrentFactData<NonNullable<typeof jobs.data>>(queryKeys.jobsFor(owner, pet), enabled);
  const collectionData = useCurrentFactData<NonNullable<typeof collection.data>>(collectionQueryKey, collectionEnabled);
  const shotData = useCurrentFactData<NonNullable<typeof photos.data>>(queryKeys.photoRequestsFor(owner, pet), enabled);
  const albumData = useCurrentFactData<NonNullable<typeof album.data>>(albumCountKey(owner, pet), enabled);
  const guideData = useCurrentFactData<NonNullable<typeof guides.data>>(guidesQueryKey, guidesEnabled);
  const shots = shotData ? photoCounts(shotData) : null;
  const count = (n: number, unit: string, none: string): Fact => ({ main: n > 0 ? <><b>{n}</b> {unit}</> : none });
  return {
    jobs: jobData ? jobFact(jobData) : null,
    collection: collectionData ? count(collectionData.length, "件", "还空着") : null,
    // 大字数相册（画好的）；小字说拍照记录里还在画的几张——在画的不算照片
    photos: albumData
      ? {
          main: albumData.total_visible > 0 ? <><b>{albumData.total_visible}</b> 张</> : "还没有照片",
          detail: shots && shots.drawing > 0 ? `还有 ${shots.drawing} 张在画` : undefined,
        }
      : null,
    guides: guideData ? guideFact(guideData.length, wish, demo, petName) : null,
  };
}

/** A retry/cancel can revert transient error fields, but cannot confer new read authority. */
const failedFactReads = new WeakMap<object, number>();

export function useCurrentFactData<T>(queryKey: readonly unknown[], enabled: boolean): T | undefined {
  const client = useQueryClient();
  const scope = JSON.stringify(queryKey);
  const subscribe = useCallback((changed: () => void) => client.getQueryCache().subscribe(event => {
    if (JSON.stringify(event.query.queryKey) !== scope) return;
    const state = event.query.state;
    if (state.status === "error" || state.fetchFailureCount > 0 || state.fetchFailureReason !== null) {
      failedFactReads.set(event.query, state.dataUpdateCount);
    }
    changed();
  }), [client, scope]);
  const snapshot = useCallback(() => {
    const query = client.getQueryCache().find<T>({ queryKey, exact: true });
    if (!query) return undefined;
    const state = query.state;
    if (state.status === "error" || state.fetchFailureCount > 0 || state.fetchFailureReason !== null) {
      failedFactReads.set(query, state.dataUpdateCount);
      return undefined;
    }
    const failedAt = failedFactReads.get(query);
    if (failedAt !== undefined) {
      // Revert restores the same committed-success counter; even identical real successes increment it.
      if (state.status !== "success" || state.dataUpdateCount <= failedAt) return undefined;
      failedFactReads.delete(query);
    }
    return enabled && state.status === "success" ? state.data : undefined;
  }, [client, scope, enabled]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * 旅程与攻略手账，和攻略手账页一致：旧攻略份数，加上攻略页“想去 / 准备中”里写好了的计划——
 * 当前心愿已发布的计划（计划发布时就写好了计划手账，算一份；只有 plan_id、plan_revision 还空着的不算，见 ./travelMemory 的 publishedPlanId），
 * 演示时再加上其余几份演示计划（同攻略页：和当前心愿是同一条的不重复算；演示计划只在演示模式有）。
 * 小字是攻略手账页上那张心愿卡的标题（travelPlan/model 的 viewOfWish，同一套说法，如“小游 准备去 浅水湾”）。
 * 心愿、演示计划还在读，或心愿读不到：这一句不写（数不全，不拿旧攻略的份数充数）；没有心愿就不写小字。
 */
export function guideFact(guideCount: number, wish: Read<TravelWish | null>, demo: Read<readonly PlanBundle[]>, petName: string | null): Fact | null {
  if (wish.state === "loading" || wish.state === "error" || demo.state === "loading") return null;
  const current = wish.state === "ready" ? wish.data : null;
  const demoPlans = demo.state === "ready" ? demo.data.filter((bundle) => bundle.wish.wish_id !== current?.wish_id).length : 0;
  const n = guideCount + (publishedPlanId(current) ? 1 : 0) + demoPlans;
  return { main: n > 0 ? <><b>{n}</b> 份</> : "还没有手账", detail: current ? viewOfWish(current, { petName }).title : undefined };
}

/** 最近一条打工：状态在前（去上班的路上 / 在干活 / 干完了），岗位名作小字；不认识的状态不猜，只说“最近一份工”。 */
function jobFact(list: JobRecord[]): Fact {
  const latest = latestJob(list);
  if (!latest) return { main: "还没有打工记录" };
  return { main: jobStatusText(latest.status) ?? "最近一份工", detail: latest.title };
}

function EntryCard({ to, icon, tone, title, desc, fact, wide = false, aside = null }: {
  to: string;
  icon: IconName;
  tone: Tone;
  title: string;
  desc: string;
  fact: Fact | null;
  wide?: boolean;
  aside?: ReactNode;
}) {
  const location = useLocation();
  const jobsState = to === "/life?tab=jobs&from=memories" ? {
    ...preserveSceneState(location.state),
    sceneEntry: { version: 1, parent: "/memories", origin: readSceneEntry(location.state, "/memories")?.origin ?? "/memories" },
  } : undefined;
  return (
    <SourceLink to={to} state={jobsState} className={`ps-mem-card${wide ? " ps-mem-card--wide" : ""}`} data-tone={tone}>
      <span className="ps-mem-stamp" aria-hidden="true">
        <Icon name={icon} size={22} />
      </span>
      <span className="ps-mem-card__body">
        <span className="ps-mem-card__title">{title}</span>
        <span className="ps-mem-card__desc">{desc}</span>
        {fact ? (
          <span className="ps-mem-card__fact">
            <span className="ps-mem-card__main">{fact.main}</span>
            {fact.detail ? <span className="ps-mem-card__detail">{fact.detail}</span> : null}
          </span>
        ) : null}
      </span>
      {aside}
      <Icon name="chevron" size={16} className="ps-mem-card__go" />
    </SourceLink>
  );
}

